import type { Cluster } from './types'

/**
 * Which sites and cameras a dispatcher has chosen to watch.
 *
 * `sites: null` is "no filter" — a state of its own, distinct from every site
 * being selected. Only it silently picks up a site added later, and only it is
 * what clearing returns to. An empty selection means nothing is shown, never
 * everything; that is the same distinction `events.SiteScope` draws on the
 * server, and for the same reason: a collection standing in for "no opinion" is
 * one refactor away from meaning its own opposite.
 *
 * `cameras` is keyed by site and survives its site being deselected, so turning
 * a site off and on again restores what was chosen inside it. A site with no
 * entry has all of its cameras.
 */
export interface SiteFilter {
  readonly sites: ReadonlySet<string> | null
  readonly cameras: Readonly<Record<string, ReadonlySet<string>>>
}

export const NO_FILTER: SiteFilter = { sites: null, cameras: {} }

/** Whether the filter narrows anything at all — a site choice or a camera one. */
export function isNarrowed(f: SiteFilter): boolean {
  return f.sites !== null || Object.keys(f.cameras).length > 0
}

export function allowsSite(f: SiteFilter, siteId: string): boolean {
  return f.sites === null || f.sites.has(siteId)
}

export function allowsCamera(f: SiteFilter, siteId: string, camera: string): boolean {
  if (!allowsSite(f, siteId)) return false
  const chosen = f.cameras[siteId]
  // No choice recorded for this site means all of its cameras, not none.
  return chosen === undefined || chosen.has(camera)
}

export function filterClusters(clusters: Cluster[], f: SiteFilter): Cluster[] {
  if (!isNarrowed(f)) return clusters
  return clusters.filter((c) => allowsCamera(f, c.site_id, c.camera))
}

/**
 * Turns one site on or off. `allSites` is what the filter starts from on the
 * first toggle: narrowing begins from everything selected, so the first click
 * removes one site rather than leaving only it.
 */
export function toggleSite(f: SiteFilter, siteId: string, allSites: string[]): SiteFilter {
  const next = new Set(f.sites ?? allSites)
  if (next.has(siteId)) next.delete(siteId)
  else next.add(siteId)
  // A selection covering every site excludes nothing, which is what no filter
  // means. Keeping it as a filter would leave the UI announcing one while
  // hiding nothing, and "Clear" with nothing to do.
  return normalise({ sites: coversAll(next, allSites) ? null : next, cameras: f.cameras })
}

/**
 * Turns one camera on or off within a site. `allCameras` is that site's full
 * list, for the same reason `allSites` is above.
 */
export function toggleCamera(
  f: SiteFilter,
  siteId: string,
  camera: string,
  allCameras: string[],
): SiteFilter {
  const next = new Set(f.cameras[siteId] ?? allCameras)
  if (next.has(camera)) next.delete(camera)
  else next.add(camera)

  const cameras = { ...f.cameras }
  if (coversAll(next, allCameras)) {
    // No entry means all of this site's cameras, so a set covering them all is
    // the same thing said twice — and leaving it behind would keep the filter
    // looking active after everything had been ticked back on.
    delete cameras[siteId]
  } else {
    cameras[siteId] = next
  }

  return normalise({
    // Left alone on purpose. Unticking a camera is a statement about that
    // camera; narrowing the site selection here would silently hide every other
    // site the dispatcher never touched.
    sites: f.sites,
    cameras,
  })
}

function coversAll(chosen: ReadonlySet<string>, all: string[]): boolean {
  return all.length > 0 && all.every((id) => chosen.has(id))
}

/** Collapses a filter that excludes nothing back to the default. */
function normalise(f: SiteFilter): SiteFilter {
  return isNarrowed(f) ? f : NO_FILTER
}
